import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Container from "@/components/Container";
import { api } from "@/lib/api";
import { safeImg } from "@/lib/safeImg";
import { ArrowLeft, ArrowUpRight } from "lucide-react";

export default function PropertyPublicDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.get(`/public/properties/${id}`).then(r => setP(r.data)).catch(() => setErr(true));
  }, [id]);
  if (err) return (
    <Container className="pt-40 pb-24">
      <h1 className="serif-display text-5xl text-ink-700">Project not found</h1>
      <Link to="/properties" className="btn-link mt-8 inline-flex">← Back to Portfolio</Link>
    </Container>
  );
  if (!p) return <Container className="pt-40 text-ink-100">Loading…</Container>;
  const img = safeImg(p.public_main_image || p.images?.[0], "");
  return (
    <div className="pt-32">
      <Container className="pb-12">
        <Link to="/properties" className="inline-flex items-center gap-2 text-sm text-ink-400 hover:text-ruby transition-colors">
          <ArrowLeft size={14} /> Back to portfolio
        </Link>
        <div className="mt-8 flex items-center gap-4">
          <span className="label-mono">{(p.public_status || "Project").replace(/_/g, " ")}</span>
          <span className="h-px w-10 bg-ruby" />
        </div>
        <h1 className="mt-6 serif-display text-5xl md:text-7xl lg:text-[7rem] text-ink-700">
          {p.public_project_name || p.title}
        </h1>
        <p className="mt-6 text-ink-400 text-lg lg:text-xl">{p.city || ""} {p.county ? `· ${p.county}` : ""} {p.postcode ? `· ${p.postcode}` : ""}</p>
      </Container>
      {img && (
        <div className="w-full aspect-[16/9] max-h-[80vh] overflow-hidden">
          <img src={img} alt={p.title} className="w-full h-full object-cover" />
        </div>
      )}
      <Container className="py-20 grid grid-cols-1 md:grid-cols-3 gap-16">
        <div className="md:col-span-2">
          <p className="label-mono">The Description</p>
          <p className="mt-6 text-ink-400 leading-[1.85] whitespace-pre-line text-base lg:text-lg max-w-2xl">
            {p.public_short_description || p.description || "Details coming soon."}
          </p>
        </div>
        <div className="surface-cream p-8 h-fit border-t-2 border-ruby">
          <p className="label-mono">Specification</p>
          <dl className="mt-6 space-y-4 text-sm">
            {p.bedrooms != null && <Row k="Bedrooms" v={p.bedrooms} />}
            {p.bathrooms != null && <Row k="Bathrooms" v={p.bathrooms} />}
            {p.property_type && <Row k="Type" v={p.property_type} />}
            {p.tenure && <Row k="Tenure" v={p.tenure} />}
            {p.epc_rating && <Row k="EPC" v={p.epc_rating} />}
            {p.floor_area && <Row k="Floor area" v={`${p.floor_area} sqft`} />}
          </dl>
          <div className="divider-ruby-h my-8" />
          <Link to="/contact" className="btn-ruby-solid w-full justify-center">Enquire <ArrowUpRight size={14} /></Link>
        </div>
      </Container>
      {p.images?.length > 1 && (
        <Container className="pb-20 grid grid-cols-1 md:grid-cols-2 gap-4">
          {p.images.slice(1).map((src, i) => <img key={i} src={src} alt="" className="w-full aspect-[4/3] object-cover" />)}
        </Container>
      )}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between border-b border-ink-700/15 pb-3">
      <dt className="label-mono-mute">{k}</dt>
      <dd className="text-ink-700 serif-light text-base">{String(v)}</dd>
    </div>
  );
}
